import { ChecklistFieldDef } from "../components/DynamicChecklistManagement/DynamicChecklistManagement";
import { normalizeDynamicTagId } from "./dynamicChecklistScrutinyPrefill";

export const LEGAL_HEIRS_TAG_IDS = new Set([
  "legal_hiers",
  "legal_heirs",
  "legai_heirs",
]);

/** Row meta: how many numbered legal-heir inputs to show for a deed entry. */
export const LEGAL_HEIR_COUNT_META_KEY = "__legal_heir_count__";

export function isLegalHeirsMappedField(field: ChecklistFieldDef): boolean {
  if (field.dynamicValueSource === "front_office") return false;
  const tag = normalizeDynamicTagId(field.scrutinyLinkedDeedId).toLowerCase();
  return LEGAL_HEIRS_TAG_IDS.has(tag);
}

export function legalHeirValueKey(fieldId: string, index: number): string {
  return `${fieldId}__legal_heir__${index}`;
}

export function parseLegalHeirsListFromScrutiny(row: Record<string, string>): string[] {
  const jsonRaw = String(row.legal_heirs_list_json ?? "").trim();
  if (jsonRaw.startsWith("[")) {
    try {
      const parsed = JSON.parse(jsonRaw);
      if (Array.isArray(parsed)) {
        return parsed.map((v) => String(v ?? "").trim()).filter((v) => v !== "");
      }
    } catch {
      /* fall through */
    }
  }
  const combined = String(
    row.legal_hiers ?? row.legal_heirs ?? row.legai_heirs ?? ""
  ).trim();
  if (!combined) return [];
  return combined
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s !== "");
}

export function getLegalHeirInputCount(
  row: Record<string, string>,
  fieldId: string
): number {
  const meta = parseInt(String(row[LEGAL_HEIR_COUNT_META_KEY] ?? ""), 10);
  if (Number.isFinite(meta) && meta > 0) return meta;

  let maxIdx = -1;
  const prefix = `${fieldId}__legal_heir__`;
  Object.keys(row).forEach((k) => {
    if (!k.startsWith(prefix)) return;
    const idx = parseInt(k.slice(prefix.length), 10);
    if (Number.isFinite(idx) && idx > maxIdx) maxIdx = idx;
  });
  if (maxIdx >= 0) return maxIdx + 1;

  const legacy = String(row[fieldId] ?? "").trim();
  if (legacy) {
    const parts = legacy.split(",").map((s) => s.trim()).filter(Boolean);
    return Math.max(parts.length, 1);
  }
  return 1;
}

export function applyLegalHeirsToRow(
  row: Record<string, string>,
  fieldId: string,
  heirs: string[]
): void {
  const count = Math.max(heirs.length, 1);
  row[LEGAL_HEIR_COUNT_META_KEY] = String(count);
  for (let i = 0; i < count; i++) {
    row[legalHeirValueKey(fieldId, i)] = heirs[i] ?? "";
  }
  row[fieldId] = "";
}

/**
 * Merge saved checklist legal-heir answers with document data.
 * Keeps each saved value; only fills empty slots and extends the list when
 * the document has additional heirs.
 */
export function mergeLegalHeirsPrefill(
  row: Record<string, string>,
  fieldId: string,
  documentHeirs: string[]
): void {
  const savedCount = getLegalHeirInputCount(row, fieldId);
  const saved: string[] = [];
  for (let i = 0; i < savedCount; i++) {
    saved.push(String(row[legalHeirValueKey(fieldId, i)] ?? ""));
  }
  const count = Math.max(saved.length, documentHeirs.length, 1);
  row[LEGAL_HEIR_COUNT_META_KEY] = String(count);
  for (let i = 0; i < count; i++) {
    const savedVal = String(saved[i] ?? "").trim();
    const docVal = String(documentHeirs[i] ?? "").trim();
    row[legalHeirValueKey(fieldId, i)] = savedVal || docVal;
  }
  row[fieldId] = "";
}

export function collectLegalHeirsFromRow(
  row: Record<string, string>,
  fieldId: string
): string[] {
  const count = getLegalHeirInputCount(row, fieldId);
  const names: string[] = [];
  for (let i = 0; i < count; i++) {
    const val = String(row[legalHeirValueKey(fieldId, i)] ?? "").trim();
    if (val !== "") names.push(val);
  }
  if (names.length) return names;

  const legacy = String(row[fieldId] ?? "").trim();
  if (!legacy) return [];
  return legacy
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s !== "");
}

export function legalHeirsRowHasValue(
  row: Record<string, string>,
  fieldId: string
): boolean {
  return collectLegalHeirsFromRow(row, fieldId).length > 0;
}
